// Timeline date helpers. Dates are stored as "YYYY", "YYYY-MM" or "YYYY-MM-DD"
// strings; the timeline works per day (day = days since 1970-01-01, UTC). Like
// v1, a partial date covers its whole period: an element added "2024-03" shows
// from 1 March, and one removed "2024-03" is still there until 31 March.

// Date fields take a year, a year and month, or a full date — "2026",
// "2026-4" / "2026-04", "2026-04-15" (also with / or . between) — and store
// the canonical "YYYY" / "YYYY-MM" / "YYYY-MM-DD". Returns null for an empty
// field, undefined for anything that isn't a real date.
export const DATE_PLACEHOLDER = 'YYYY-MM-DD';
export const DATE_HINT = 'A year, a year and month, or a full date — e.g. 2026, 2026-04 or 2026-04-15';

export function normalizeDate(input) {
  const s = String(input ?? '').trim();
  if (!s) return null;
  const m = s.match(/^(\d{4})(?:[-/.](\d{1,2})(?:[-/.](\d{1,2}))?)?$/);
  if (!m) return undefined;
  const [, y, mo, d] = m;
  if (!mo) return y;
  const month = Number(mo);
  if (month < 1 || month > 12) return undefined;
  const mm = String(month).padStart(2, '0');
  if (!d) return `${y}-${mm}`;
  const day = Number(d);
  const daysInMonth = new Date(Date.UTC(Number(y), month, 0)).getUTCDate();
  if (day < 1 || day > daysInMonth) return undefined;
  return `${y}-${mm}-${String(day).padStart(2, '0')}`;
}

const DAY_MS = 86400000;
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const utcDay = (y, m, d) => Math.round(Date.UTC(y, m - 1, d) / DAY_MS); // m is 1-based; overflow rolls over

function parts(s) {
  if (!s) return null;
  const m = String(s).match(/^(\d{4})(?:-(\d{1,2})(?:-(\d{1,2}))?)?/);
  if (!m) return null;
  return { y: Number(m[1]), m: m[2] ? Number(m[2]) : null, d: m[3] ? Number(m[3]) : null };
}

// First and last day a stored date covers ("2024" → 1 Jan … 31 Dec 2024).
export function dateStart(s) {
  const p = parts(s);
  if (!p) return null;
  return utcDay(p.y, p.m || 1, p.d || 1);
}

export function dateEnd(s) {
  const p = parts(s);
  if (!p) return null;
  if (p.d) return utcDay(p.y, p.m, p.d);
  if (p.m) return utcDay(p.y, p.m + 1, 0);
  return utcDay(p.y, 12, 31);
}

export function dayToDate(day) {
  if (day == null) return null;
  return new Date(day * DAY_MS).toISOString().slice(0, 10);
}

// "15 Mar 2024" for a day; stored dates keep their precision ("Mar 2024", "2024").
export function dayLabel(day) {
  if (day == null) return '';
  const d = new Date(day * DAY_MS);
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

export function dateLabel(s) {
  const p = parts(s);
  if (!p) return '';
  if (p.d) return dayLabel(utcDay(p.y, p.m, p.d));
  if (p.m) return `${MONTHS[p.m - 1]} ${p.y}`;
  return String(p.y);
}

export function todayDay() {
  // Date is unavailable in some sandboxes; guard.
  try { const d = new Date(); return utcDay(d.getFullYear(), d.getMonth() + 1, d.getDate()); } catch (e) { return null; }
}

// An element is visible on a day if it has been added by then and its removal
// date hasn't passed yet (the removal day/month itself still shows it).
export function visibleAtDay(el, day) {
  if (day == null) return true;
  const added = dateStart(el.data?.addedDate);
  const removed = dateEnd(el.data?.removedDate);
  if (added != null && added > day) return false;
  if (removed != null && removed < day) return false;
  return true;
}

// The timeline's span, from the plan's own dates (as v1): `earliest`/`latest`
// are the extreme stored dates, and the slider runs from the start of the month
// before the earliest to the end of the month after the latest, so you can see
// the garden before the first planting and after the last removal. Null when no
// element has a date.
export function timelineRange(elements) {
  let first = Infinity; let last = -Infinity;
  let earliest = null; let latest = null;
  for (const el of elements) {
    for (const key of ['addedDate', 'removedDate']) {
      const s = el.data?.[key];
      const start = dateStart(s);
      if (start == null) continue;
      const end = dateEnd(s);
      if (start < first) { first = start; earliest = s; }
      if (end > last) { last = end; latest = s; }
    }
  }
  if (!isFinite(first)) return null;
  const f = new Date(first * DAY_MS); const l = new Date(last * DAY_MS);
  return {
    earliest, latest,
    min: utcDay(f.getUTCFullYear(), f.getUTCMonth(), 1),      // 1st of the month before
    max: utcDay(l.getUTCFullYear(), l.getUTCMonth() + 3, 0),  // last day of the month after
  };
}

export function clampDay(day, range) {
  return Math.min(range.max, Math.max(range.min, day));
}
